import axiosInstance from "utils/axios";
import { toast } from "sonner";
import { getCompanyIdFromToken, getUserIdFromToken } from "../utils/tokenUtils";
import { convertToUTC, toUTCISOString, getLocalISODate } from "../utils/dateUtils";
import { uploadAttachments, DocumentUploadFeature } from "./documentUploadService";


/**
 * Utility to strip spaces from search field if it's a numeric search (e.g. phone number).
 */
const cleanSearchValue = (val) => {
  if (typeof val !== 'string') return val;
  const stripped = val.replace(/\s+/g, '');
  if (/^[+\d\-().\s]+$/.test(val) && /\d/.test(val)) {
    return stripped;
  }
  return val;
};

// Channel mapping for follow-up types
const getChannelId = (type) => {
  const channelMap = {
    call: 1,
    email: 2,
    meeting: 3,
    whatsapp: 4,
    sms: 5
  };
  return channelMap[type] || 1; // Default to call
};


// Mock data for development - replace with actual API calls
let mockFollowUps = [

];



// Create a new follow-up
export const createFollowUp = async (followUpData) => {
  try {
    // Use local selection instead of converting to UTC
    const utcDate = followUpData.date ? getLocalISODate(followUpData.date) : null;
    const utcTime = followUpData.time || "09:00";

    // Prepare the API payload
    const apiPayload = {
      leadId: followUpData.leadId,
      assignedTo: followUpData.assignedTo || getUserIdFromToken(),
      scheduledDate: utcDate, // Send UTC Day
      scheduledTime: utcTime, // Send UTC Time
      channelId: getChannelId(followUpData.channelId || "call"),
      notes: followUpData.notes || "",
      LeadActivityTypeId: followUpData.LeadActivityTypeId || null,
    };


    // Make the API call
    const response = await axiosInstance.post("/api/FollowUp/CreateFollowUp", apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-up scheduled successfully!", {
        description: `Follow-up has been scheduled for ${followUpData.date ? new Date(followUpData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'today'} at ${followUpData.time ? (parseInt(followUpData.time.split(':')[0]) % 12 || 12) + ':' + followUpData.time.split(':')[1] + (parseInt(followUpData.time.split(':')[0]) >= 12 ? ' PM' : ' AM') : '9:00 AM'}`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-up scheduled successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to schedule follow-up");
    }
  } catch (error) {
    console.error("Error creating follow-up:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to schedule follow-up";

    toast.error("Failed to schedule follow-up", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};



// Create multiple follow-ups (new API)
export const createMultipleFollowUp = async (followUpData) => {
  try {
    // Use local selection instead of converting to UTC
    const utcDate = followUpData.date ? getLocalISODate(followUpData.date) : null;
    const utcTime = followUpData.time || "09:00";

    // Use preferred date/time as local if present
    let utcPrefDate = null;
    let utcPrefTime = null;
    if (followUpData.preferredDeliveryDate) {
      utcPrefDate = getLocalISODate(followUpData.preferredDeliveryDate);
      utcPrefTime = followUpData.preferredDeliveryTime || "09:00";
    }

    const isEmail = followUpData.channelId === 1;
    const isSms = followUpData.channelId === 2;

    // Map attendees to array
    let attendeeIds = [];
    if (followUpData.attendees) {
      if (Array.isArray(followUpData.attendees)) {
        attendeeIds = followUpData.attendees;
      } else if (typeof followUpData.attendees === 'string') {
        attendeeIds = followUpData.attendees.split(',').filter(id => id.trim() !== '');
      }
    }

    // Parse reminder type to minutes (null = No Reminder)
    let reminderBeforeMinutes = null;
    if (followUpData.reminderType && followUpData.reminderType !== "none") {
      if (followUpData.reminderType === "5m") reminderBeforeMinutes = 5;
      else if (followUpData.reminderType === "15m") reminderBeforeMinutes = 15;
      else if (followUpData.reminderType === "30m") reminderBeforeMinutes = 30;
      else if (followUpData.reminderType === "1h") reminderBeforeMinutes = 60;
      else if (followUpData.reminderType === "1d") reminderBeforeMinutes = 1440;
    }

    // Resolve attachments: real Files get uploaded directly to storage via presigned URLs;
    // anything already carrying a filePath (e.g. kept from a prior save on edit) passes through as-is.
    const rawAttachments = Array.isArray(followUpData.attachments) ? followUpData.attachments : [];
    const filesToUpload = rawAttachments.filter(a => a instanceof File);
    const existingAttachments = rawAttachments
      .filter(a => !(a instanceof File))
      .map(a => ({
        fileName: a?.FileName || a?.fileName,
        filePath: a?.FilePath || a?.filePath || a?.path || a?.url,
        fileExtension: a?.FileExtension || a?.fileExtension,
      }))
      .filter(a => a.filePath);

    let uploadedAttachments = [];
    if (filesToUpload.length > 0) {
      const uploadResult = await uploadAttachments(DocumentUploadFeature.ACTIVITY_ATTACHMENT, filesToUpload);
      if (!uploadResult.success) {
        throw new Error(uploadResult.message || "Failed to upload attachments");
      }
      uploadedAttachments = uploadResult.data.map((item, idx) => {
        const bytes = filesToUpload[idx]?.size || (item.fileSizeKB ? item.fileSizeKB * 1024 : 0);
        return {
          fileName: item.fileName,
          filePath: item.filePath,
          fileExtension: item.fileExtension,
          fileBytes: bytes,
          fileSizeBytes: bytes
        };
      });
    }

    // Prepare the API payload
    const apiPayload = {
      ActivityName: followUpData.activityName,
      LeadIds: Array.isArray(followUpData.leadId) ? followUpData.leadId : [followUpData.leadId],
      Leads: followUpData.leads || [],
      AssignedTo: followUpData.assignedTo || getUserIdFromToken(),
      ScheduledDate: utcDate,
      ScheduledTime: utcTime,
      ChannelId: followUpData.channelId,
      Priority: followUpData.priority || "Normal",
      Remarks: followUpData.notes || "",
      PreferredDeliveryDate: utcPrefDate,
      PreferredDeliveryTime: utcPrefTime,
      ReminderBeforeMinutes: reminderBeforeMinutes,
      // Email / SMS fields
      Subject: followUpData.subject || "",
      Body: followUpData.body || "",
      CC: Array.isArray(followUpData.cc) ? followUpData.cc.join(',') : (followUpData.cc || ""),
      Bcc: Array.isArray(followUpData.bcc) ? followUpData.bcc.join(',') : (followUpData.bcc || ""),
      Attachments: [...existingAttachments, ...uploadedAttachments],
      TemplateId: followUpData.templateId || null,
      Message: (isSms || isEmail) ? (followUpData.body || "") : "",
      // Specific activity fields
      PhoneNumber: followUpData.phoneNumber || "",
      Location: followUpData.address || "",

      MeetingURL: followUpData.meetingUrl || "",
      AttendeeIds: attendeeIds,
      LeadActivityTitle: followUpData.title || "",
      LeadActivityNotes: followUpData.leadActivityNotes,
      FormattedLeadActivityNotes: followUpData.formattedLeadActivityNotes || followUpData.formattedleadactivitynotes || "",
      IncludeLeadInActivity: followUpData.IncludeLeadInActivity !== undefined ? followUpData.IncludeLeadInActivity : true,
      LeadActivityTypeId: followUpData.LeadActivityTypeId || null,
    };

    // Make the API call
    const response = await axiosInstance.post("/api/FollowUp/CreateMultipleFollowUp", apiPayload);

    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Activities scheduled successfully!", {
        description: `Scheduled for ${apiPayload.LeadIds.length} lead(s) on ${followUpData.date ? new Date(followUpData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'today'} at ${followUpData.time ? (parseInt(followUpData.time.split(':')[0]) % 12 || 12) + ':' + followUpData.time.split(':')[1] + (parseInt(followUpData.time.split(':')[0]) >= 12 ? ' PM' : ' AM') : '9:00 AM'}`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Activities scheduled successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to schedule activities");
    }
  } catch (error) {
    console.error("Error creating multiple follow-ups:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to schedule activities";

    toast.error("Failed to schedule activities", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Get follow-ups for a specific lead
export const getLeadFollowUps = async (leadId) => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      const leadFollowUps = mockFollowUps.filter(
        followUp => followUp.leadId === leadId
      );

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        data: leadFollowUps,
      };
    }

    // Production API call
    const response = await axiosInstance.get(`/api/followups/lead/${leadId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching lead follow-ups:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch follow-ups");
  }
};

/**
 * Get follow-ups list for the Activities List view.
 * POST /api/FollowUp/GetFollowUps with body { type, pageNumber, pageSize }.
 *
 * @param {Object} [params={}]
 * @param {number} [params.type=0]        – 0 = All, 1 = Leads, 2 = Applicants
 * @param {number} [params.pageNumber=0]  – page index
 * @param {number} [params.pageSize=0]    – records per page (0 = all)
 */
export const getFollowUps = async ({
  type = 0,
  
  pageNumber = 0,
  pageSize = 0,
} = {}) => {
  try {
    // ── Dev: return mock data ──────────────────────────────────────────
    if (process.env.NODE_ENV === "development") {
      await new Promise(resolve => setTimeout(resolve, 500));
      return { success: true, data: [...mockFollowUps], totalRecords: mockFollowUps.length };
    }

    // ── Production: POST with payload ─────────────────────────────────
    const response = await axiosInstance.post(
      "/api/FollowUp/GetFollowUps",
      { type, pageNumber, pageSize }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Backend may return data as a JSON string - parse it if needed
      let raw = responseData.data;
      if (typeof raw === "string") {
        try { raw = JSON.parse(raw); } catch (_) { raw = []; }
      }

      const list = Array.isArray(raw) ? raw : [];

      const channelToType = {
        1: "Call", 2: "Email", 3: "Meeting", 4: "Site Visit", 5: "SMS",
      };

      // Normalise to the shape ActivitiesList table columns expect
      const data = list.map((item) => ({
        // identity
        id: item.FollowUpId || item.followUpId || item.Id,
        leadId: item.LeadId || item.leadId,
        // table columns
        status: item.StatusName || item.status || "Pending",
        type:
          item.ChannelName ||
          item.channelName ||
          channelToType[item.ChannelId || item.channelId] ||
          "Call",
        title: item.LeadName || item.leadName || item.Title || item.title || "-",
        assignedUser: item.AssignedTo || item.assignedTo || item.AssignedToName || "-",
        attendees: item.Attendees || item.attendees || null,
        scheduledDate: item.ScheduledDate
          ? new Date(item.ScheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : item.scheduledDate
          ? new Date(item.scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
          : "-",
        notes: item.Notes || item.notes || item.Remarks || item.remarks || "",
        cellPhone: item.PhoneNumber || item.phoneNumber || item.CellPhone || item.cellPhone || "",
        suburb: item.Suburb || item.suburb || "",
        emailAddress: item.Email || item.email || item.EmailAddress || item.emailAddress || "",
        lastContacted: item.LastContactedDate || item.lastContactedDate || "",
        phone: item.Phone || item.phone || "",
        state: item.State || item.state || "",
        streetAddress: item.StreetAddress || item.streetAddress || item.Address || item.address || "",
        postalCode: item.PostalCode || item.postalCode || item.ZipCode || item.zipCode || "",
        // keep raw for edit modal pre-fill
        _raw: item,
      }));

      return {
        success: true,
        data,
        totalRecords: responseData.totalRecords ?? list.length,
      };
    }

    throw new Error(responseData.message || "Failed to fetch activities list");
  } catch (error) {
    console.error("Error fetching follow-ups:", error);
    throw new Error(error.response?.data?.message || error.message || "Failed to fetch follow-ups");
  }
};


// Update follow-up (full update using new POST endpoint)
export const updateFollowUp = async (followUpId, followUpData) => {
  try {
    // Use local selection instead of converting to UTC
    const utcDate = followUpData.date ? getLocalISODate(followUpData.date) : null;
    let utcTime = followUpData.time || "09:00";
    if (utcTime.split(':').length === 2) utcTime += ":00";

    // Use preferred date/time as local if present
    let utcPrefDate = null;
    let utcPrefTime = null;
    if (followUpData.preferredDeliveryDate) {
      utcPrefDate = getLocalISODate(followUpData.preferredDeliveryDate);
      utcPrefTime = followUpData.preferredDeliveryTime || "09:00";
      if (utcPrefTime.split(':').length === 2) utcPrefTime += ":00";
    }

    // Prepare the API payload according to the new API structure
    const apiPayload = {
      followUpId: followUpId,
      notes: followUpData.notes || followUpData.remarks || "",
      assignedTo: followUpData.assignedTo || getUserIdFromToken(),
      newScheduledDate: utcDate,
      newScheduledTime: utcTime,
      channelId: followUpData.channelId || getChannelId(followUpData.type || "call"), // Prefer original channelId; fall back to type mapping
      updatedBy: followUpData.updatedBy || getUserIdFromToken(),
      activityName: followUpData.activityName || followUpData.type,
      ActivityName: followUpData.activityName || followUpData.type,
      title: followUpData.title || "",
      LeadActivityTitle: followUpData.leadActivityTitle || "",
      PreferredDeliveryDate: utcPrefDate,
      PreferredDeliveryTime: utcPrefTime,
      LeadActivityNotes: followUpData.leadActivityNotes,
      FormattedLeadActivityNotes: followUpData.formattedLeadActivityNotes || followUpData.formattedleadactivitynotes || "",
      statusId: followUpData.statusId || null,
      IncludeLeadInActivity: followUpData.IncludeLeadInActivity !== undefined ? followUpData.IncludeLeadInActivity : true,
    };


    // Make the API call using new POST endpoint
    const response = await axiosInstance.post('/api/FollowUp/UpdateFollowUp', apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-up updated successfully!", {
        description: `Follow-up has been updated for ${followUpData.date ? new Date(followUpData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'today'} at ${followUpData.time ? (parseInt(followUpData.time.split(':')[0]) % 12 || 12) + ':' + followUpData.time.split(':')[1] + (parseInt(followUpData.time.split(':')[0]) >= 12 ? ' PM' : ' AM') : '9:00 AM'}`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-up updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update follow-up");
    }
  } catch (error) {
    console.error("Error updating follow-up:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update follow-up";

    toast.error("Failed to update follow-up", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Update follow-up status (using new POST endpoint)
export const updateFollowUpStatus = async (followUpId, statusData = {}) => {
  try {

    // Skip mock data and always use real API for status updates
    // if (process.env.NODE_ENV === "development") {
    //   ... mock code removed to force real API calls
    // }

    let utcDate = null;
    let utcTime = null;

    if (statusData.newScheduledDate) {
      utcDate = getLocalISODate(statusData.newScheduledDate);
      let stTime = statusData.newScheduledTime || "09:00";
      if (stTime.split(':').length === 2) stTime += ":00";
      utcTime = stTime;
    }

    let fallbackTime = statusData.newScheduledTime || "09:00";
    if (fallbackTime.split(':').length === 2) fallbackTime += ":00";

    // Prepare API payload according to the new API structure
    const apiPayload = {
      followUpId: followUpId,
      eventTypeId: statusData.eventTypeId || statusData.outcomeId || "3fa85f64-5717-4562-b3fc-2c963f66afa6",
      remarks: statusData.remarks || "",
      statusId: statusData.statusId || 0,
      assignedTo: statusData.assignedTo || getUserIdFromToken(),
      newScheduledDate: utcDate ? utcDate : (statusData.newScheduledDate ? getLocalISODate(statusData.newScheduledDate) : null), // Fallback
      newScheduledTime: utcTime ? utcTime : fallbackTime,
      updatedBy: getUserIdFromToken()
    };


    // Production API call using new POST endpoint
    const response = await axiosInstance.post('/api/FollowUp/UpdateFollowUp', apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-up status updated successfully!", {
        description: `Status updated successfully`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-up status updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update follow-up status");
    }
  } catch (error) {
    console.error("Error updating follow-up status:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update follow-up status";

    toast.error("Failed to update follow-up status", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Update multiple follow-ups in bulk (using new POST endpoint)
/**
 * Get follow-ups by lead ID
 * @param {string} leadId - The lead ID to fetch follow-ups for
 * @returns {Promise<Object>} API response with follow-ups data
 */
export const getFollowUpsByLeadId = async (leadId) => {
  try {
    if (!leadId) {
      throw new Error("Lead ID is required");
    }


    const response = await axiosInstance.get(`/api/FollowUp/GetFollowUpsByLeadId?LeadId=${leadId}`);
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      // Parse the data string if it's a JSON string
      let followUpsData = [];
      if (typeof responseData.data === "string") {
        try {
          followUpsData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing follow-ups data:", parseError);
          followUpsData = [];
        }
      } else if (Array.isArray(responseData.data)) {
        followUpsData = responseData.data;
      }

      // Localize scheduledTime and apply Overdue logic
      followUpsData = followUpsData.map(detail => {
        // Logic: If Pending (1) AND (Date < Today OR (Date == Today AND Time >= 20:00)) -> Overdue (6)
        let statusId = detail.StatusId;
        let statusName = detail.StatusName;

        if (statusId === 1) {
          const scheduledDate = new Date(detail.ScheduledDate);
          const today = new Date();

          // Compare dates (reset time to 00:00:00)
          const sDate = new Date(scheduledDate.getFullYear(), scheduledDate.getMonth(), scheduledDate.getDate());
          const tDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

          if (sDate < tDate) {
            statusId = 6;
            statusName = 'Overdue';
          } else if (sDate.getTime() === tDate.getTime()) {
            if (today.getHours() >= 20) {
              statusId = 6;
              statusName = 'Overdue';
            }
          }
        }

        return {
          ...detail,
          StatusId: statusId,
          StatusName: statusName,
          // Update lowercase props if they exist to maintain consistency
          statusId: statusId,
          status: statusName,
          scheduledTime: detail.ScheduledTime ? (() => {
            const [h, m] = detail.ScheduledTime.split(':');
            let hours = parseInt(h, 10);
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            return `${hours}:${m} ${ampm}`;
          })() : ''
        };
      });


      return {
        success: true,
        data: followUpsData,
        message: responseData.message,
        totalRecords: responseData.totalRecords || followUpsData.length,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch follow-ups");
    }
  } catch (error) {
    console.error("❌ Error fetching follow-ups by lead ID:", error);

    let errorMessage = "Failed to fetch follow-ups";
    if (error.response) {
      const status = error.response.status;
      const serverMessage = error.response.data?.message || error.response.data?.error;

      if (status >= 400 && status < 500) {
        errorMessage = serverMessage || `Client error: ${status}`;
      } else if (status >= 500) {
        errorMessage = serverMessage || `Server error: ${status}`;
      }
    } else if (error.request) {
      errorMessage = "Network error: Unable to connect to server";
    } else {
      errorMessage = error.message || "Unknown error occurred";
    }

    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status || null,
      data: [],
      totalRecords: 0,
    };
  }
};

export const updateMultipleFollowUps = async (followUpIds, updateData = {}) => {
  try {

    // Prepare the API payload according to the new API structure
    // Only send fields that are being updated - exclude schedule time/date for status updates
    const apiPayload = {
      followUpIds: followUpIds,
      companyId: updateData.companyId || null,
      eventTypeId: updateData.eventTypeId || null,
      remarks: updateData.remarks || "",
      assignedTo: updateData.assignedTo || null,
      statusId: updateData.statusId || null,
      updatedBy: updateData.updatedBy || getUserIdFromToken()
    };

    // Only include schedule fields if they are explicitly provided
    if (updateData.newScheduledDate) {
      apiPayload.newScheduledDate = updateData.newScheduledDate;
    }
    if (updateData.newScheduledTime) {
      let bulkTime = updateData.newScheduledTime;
      if (bulkTime.split(':').length === 2) bulkTime += ":00";
      apiPayload.newScheduledTime = bulkTime;
    }


    // Make the API call using new POST endpoint
    const response = await axiosInstance.post('/api/FollowUp/UpdateMultiplefollowUps', apiPayload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-ups updated successfully!", {
        description: `${followUpIds.length} follow-up(s) have been updated`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-ups updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update follow-ups");
    }
  } catch (error) {
    console.error("Error updating multiple follow-ups:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update follow-ups";

    toast.error("Failed to update follow-ups", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Bulk update follow-up status
export const bulkUpdateFollowUpStatus = async (followUpIds, statusData = {}) => {
  try {

    // Use the new bulk update API - only send outcome, status, and remarks
    // Do NOT send schedule time/date for status updates
    return await updateMultipleFollowUps(followUpIds, {
      statusId: statusData.statusId,
      remarks: statusData.remarks,
      assignedTo: statusData.assignedTo,
      priority: statusData.priority,
      companyId: statusData.companyId,
      eventTypeId: statusData.eventTypeId,
      updatedBy: statusData.updatedBy || getUserIdFromToken()
      // Explicitly excluding newScheduledDate and newScheduledTime
    });
  } catch (error) {
    console.error("Error bulk updating follow-up status:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to update follow-up status";

    toast.error("Failed to bulk update follow-ups", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Reschedule follow-up
export const rescheduleFollowUp = async (rescheduleData) => {
  try {

    // Use local selection instead of converting to UTC
    let utcDate = null;
    let utcTime = null;
    const targetDate = rescheduleData.date || rescheduleData.newScheduledDate;
    let targetTime = rescheduleData.time || rescheduleData.newScheduledTime || "09:00";
    if (targetDate) {
      utcDate = getLocalISODate(targetDate);
      if (targetTime.split(':').length === 2) targetTime += ":00";
      utcTime = targetTime;
    }

    // Use preferred date/time as local if present
    let utcPrefDate = null;
    let utcPrefTime = null;
    if (rescheduleData.preferredDeliveryDate) {
      utcPrefDate = getLocalISODate(rescheduleData.preferredDeliveryDate);
      let prefTime = rescheduleData.preferredDeliveryTime || "09:00";
      if (prefTime.split(':').length === 2) prefTime += ":00";
      utcPrefTime = prefTime;
    }

    const isEmail = rescheduleData.channelId === 1;
    const isSms = rescheduleData.channelId === 2;

    // Map attendees to array
    let attendeeIds = [];
    if (rescheduleData.attendees) {
      if (Array.isArray(rescheduleData.attendees)) {
        attendeeIds = rescheduleData.attendees;
      } else if (typeof rescheduleData.attendees === 'string') {
        attendeeIds = rescheduleData.attendees.split(',').filter(id => id.trim() !== '');
      }
    }

    // Parse reminder type to minutes (null = No Reminder)
    let reminderBeforeMinutes = null;
    if (rescheduleData.reminderType && rescheduleData.reminderType !== "none") {
      if (rescheduleData.reminderType === "5m") reminderBeforeMinutes = 5;
      else if (rescheduleData.reminderType === "15m") reminderBeforeMinutes = 15;
      else if (rescheduleData.reminderType === "30m") reminderBeforeMinutes = 30;
      else if (rescheduleData.reminderType === "1h") reminderBeforeMinutes = 60;
      else if (rescheduleData.reminderType === "1d") reminderBeforeMinutes = 1440;
    }

    // Prepare the API payload according to the API structure in image
    const apiPayload = {
      followUpId: rescheduleData.followUpId,
      activityName: rescheduleData.activityName || "",
      leadActivityTitle: rescheduleData.leadActivityTitle || "",
      assignedTo: rescheduleData.assignedTo || getUserIdFromToken(),
      priority: rescheduleData.priority || "Normal",
      remarks: rescheduleData.notes || rescheduleData.remarks || "",
      attendeeIds: attendeeIds,
      scheduledDate: utcDate,
      scheduledTime: utcTime,
      preferredDeliveryDate: utcPrefDate,
      preferredDeliveryTime: utcPrefTime,
      clearPreferredDelivery: rescheduleData.clearPreferredDelivery || (!utcPrefDate),
      reminderBeforeMinutes: reminderBeforeMinutes,
      clearReminderMinutes: !rescheduleData.reminderType || rescheduleData.reminderType === "none",
      leadActivityNotes: rescheduleData.leadActivityNotes || "",
      FormattedLeadActivityNotes: rescheduleData.formattedLeadActivityNotes || rescheduleData.formattedleadactivitynotes || "",
      subject: rescheduleData.subject || "",
      body: rescheduleData.body || "",
      cc: Array.isArray(rescheduleData.cc) ? rescheduleData.cc.join(',') : (rescheduleData.cc || ""),
      bcc: Array.isArray(rescheduleData.bcc) ? rescheduleData.bcc.join(',') : (rescheduleData.bcc || ""),
      templateId: rescheduleData.templateId || null,
      message: (isSms || isEmail) ? (rescheduleData.body || "") : "",
      phoneNumber: rescheduleData.phoneNumber || "",
      meetingURL: rescheduleData.meetingUrl || "",
      location: rescheduleData.address || "",
      contactIds: rescheduleData.contactIds || [],
      statusId: rescheduleData.statusId !== undefined ? rescheduleData.statusId : (rescheduleData.StatusId !== undefined ? rescheduleData.StatusId : null),
      IncludeLeadInActivity: rescheduleData.IncludeLeadInActivity !== undefined
        ? rescheduleData.IncludeLeadInActivity
        : (rescheduleData.includeLeadInActivity !== undefined
          ? rescheduleData.includeLeadInActivity
          : (rescheduleData.includeLead !== undefined ? rescheduleData.includeLead : true))
    };

    // Make the API call
    const response = await axiosInstance.post('/api/FollowUp/RescheduleFollowUp', apiPayload);

    if (response.data.statusCode === 200 && response.data.status === "Success") {
      toast.success("Follow-up rescheduled successfully!", {
        description: `Follow-up rescheduled to ${targetDate ? new Date(targetDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'selected date'} at ${targetTime ? (parseInt(targetTime.split(':')[0]) % 12 || 12) + ':' + targetTime.split(':')[1] + (parseInt(targetTime.split(':')[0]) >= 12 ? ' PM' : ' AM') : 'selected time'}`,
        duration: 4000,
      });

      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Follow-up rescheduled successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to reschedule follow-up");
    }
  } catch (error) {
    console.error("Error rescheduling follow-up:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to reschedule follow-up";

    toast.error("Failed to reschedule follow-up", {
      description: errorMessage,
      duration: 5000,
    });

    throw new Error(errorMessage);
  }
};

// Delete follow-up
export const deleteFollowUp = async (followUpId) => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      const followUpIndex = mockFollowUps.findIndex(f => f.id === followUpId);
      if (followUpIndex === -1) {
        throw new Error("Follow-up not found");
      }

      mockFollowUps.splice(followUpIndex, 1);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        message: "Follow-up deleted successfully",
      };
    }

    // Production API call
    const response = await axiosInstance.delete(`/api/followups/${followUpId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting follow-up:", error);
    throw new Error(error.response?.data?.message || "Failed to delete follow-up");
  }
};

// Get upcoming follow-ups for dashboard
export const getUpcomingFollowUps = async (days = 7) => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + days);

      const upcomingFollowUps = mockFollowUps.filter(followUp => {
        const followUpDate = new Date(followUp.date);
        return followUpDate >= today && followUpDate <= futureDate && followUp.status === "scheduled";
      });

      // Sort by date and time
      upcomingFollowUps.sort((a, b) => {
        const dateA = new Date(`${a.date.toDateString()} ${a.time}`);
        const dateB = new Date(`${b.date.toDateString()} ${b.time}`);
        return dateA - dateB;
      });

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        data: upcomingFollowUps,
      };
    }

    // Production API call
    const response = await axiosInstance.get(`/api/followups/upcoming`, { params: { days } });
    return response.data;
  } catch (error) {
    console.error("Error fetching upcoming follow-ups:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch upcoming follow-ups");
  }
};

// Get follow-up events for status updates
export const getFollowUpEvents = async () => {
  try {

    const response = await axiosInstance.get("/api/FollowUp/GetFollowUpEvents");


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // Parse the data field if it's a string
      let eventsData = response.data.data;
      if (typeof eventsData === 'string') {
        try {
          eventsData = JSON.parse(eventsData);
        } catch (parseError) {
          console.error("Error parsing events data:", parseError);
          eventsData = [];
        }
      }

      if (!Array.isArray(eventsData)) {
        eventsData = [];
      }

      // Transform the data for the dropdown
      const events = eventsData.map(event => ({
        id: event.EventTypeId,
        name: event.EventName,
        description: event.EventDescription,
        icon: event.Icon,
        defaultScore: event.DefaultScore,
        isFollowUpEvent: event.IsFollowUpEvent,
        value: event.EventTypeId,
        label: event.EventName
      }));

      return {
        success: true,
        data: events,
        totalRecords: response.data.totalRecords || eventsData.length,
        message: response.data.message || "Follow-up events fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch follow-up events");
    }
  } catch (error) {
    console.error("Error fetching follow-up events:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch follow-up events";

    toast.error("Failed to load follow-up events", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0
    };
  }
};

// Get activity types for dropdowns (new API)
export const getActivityTypes = async () => {
  try {
    const response = await axiosInstance.get("/api/FollowUp/getActivityTypes");
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let typesData = [];
      if (typeof responseData.data === "string") {
        try {
          typesData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing activity types data:", parseError);
        }
      } else if (Array.isArray(responseData.data)) {
        typesData = responseData.data;
      }
      // Map to consistent structure
      const types = typesData.map(type => {
        const id = type.Id || type.id;
        const title = type.ActivityType || type.Title || type.title;
        const description = type.Description || type.description;
        const activityType = type.ChannelId !== undefined ? type.ChannelId : (type.activityType !== undefined ? type.activityType : type.ActivityType);
        const companyId = type.CompanyId || type.companyId;

        const resolvedActivityType = typeof activityType === 'number' ? activityType : parseInt(activityType, 10);

        return {
          id,
          title,
          description,
          activityType: resolvedActivityType,
          companyId,
          
          // Backward compatibility mappings:
          ChannelId: resolvedActivityType,
          ActivityType: title,
          name: title,
          value: (title || "").toLowerCase(), // e.g. "email"
          label: title,
          channelId: resolvedActivityType
        };
      });

      return {
        success: true,
        data: types,
        totalRecords: responseData.totalRecords || types.length,
        message: responseData.message || "Activity types fetched successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch activity types");
    }
  } catch (error) {
    console.error("Error fetching activity types:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch activity types";
    
    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0
    };
  }
};

// Get follow-up statistics
export const getFollowUpStats = async () => {
  try {
    // For development, use mock data
    if (process.env.NODE_ENV === "development") {
      const stats = {
        total: mockFollowUps.length,
        scheduled: mockFollowUps.filter(f => f.status === "scheduled").length,
        completed: mockFollowUps.filter(f => f.status === "completed").length,
        overdue: mockFollowUps.filter(f => {
          const followUpDate = new Date(`${f.date.toDateString()} ${f.time}`);
          return followUpDate < new Date() && f.status === "scheduled";
        }).length,
        today: mockFollowUps.filter(f => {
          const today = new Date().toDateString();
          return f.date.toDateString() === today && f.status === "scheduled";
        }).length,
      };

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      return {
        success: true,
        data: stats,
      };
    }

    // Production API call
    const response = await axiosInstance.get("/api/followups/stats");
    return response.data;
  } catch (error) {
    console.error("Error fetching follow-up stats:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch follow-up statistics");
  }
};

// Get users for assignment dropdown (uses the new GetUsersForAssignLeads API)
export const getUsersForAssignment = async () => {
  try {
    const response = await axiosInstance.get("/api/FollowUp/GetUserToScheduleActivity");


    // Check if the response indicates failure (case-insensitive)
    if (response.data?.status === "Failure" || response.data?.statusCode >= 400) {
      throw new Error(response.data?.message || "Failed to fetch users for assignment");
    }

    if (response.data.statusCode === 200 && response.data.status?.toLowerCase() === "success") {
      // Parse the data field if it's a string
      let userData = response.data.data;
      if (typeof userData === 'string') {
        try {
          userData = JSON.parse(userData);
        } catch (parseError) {
          console.error("Error parsing user data:", parseError);
          userData = [];
        }
      }

      if (!Array.isArray(userData)) {
        userData = [];
      }

      // Transform the data for the dropdown - simpler format without query count
      const users = userData.map(user => ({
        id: user.UserId,
        name: user.FullName,
        value: user.UserId,
        label: user.FullName,
        badgeText: user.DesignationName || user.designationName || user.roleName || user.RoleName || ""
      }));

      return {
        success: true,
        data: users,
        totalRecords: response.data.totalRecords || userData.length,
        message: response.data.message || "Users fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch users");
    }
  } catch (error) {
    console.error("Error fetching users for assignment:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch users";

    toast.error("Failed to load assignment options", {
      description: errorMessage,
      duration: 5000,
    });

    // Return current user as fallback
    const currentUserId = getUserIdFromToken();
    return {
      success: false,
      error: errorMessage,
      data: currentUserId ? [{
        id: currentUserId,
        name: "Myself",
        value: currentUserId,
        label: "Myself"
      }] : [],
      totalRecords: currentUserId ? 1 : 0
    };
  }
};

// Get follow-ups by date range for calendar view (new API)
// isApplication: 0 = All, 2 = Applicants only, 1 = Leads only
export const getFollowUpsByDateRange = async (fromDate, toDate, isApplication = 0) => {
  try {

    const payload = {
      fromDate: fromDate,
      toDate: toDate,
      type: isApplication
    };

    const response = await axiosInstance.post("/api/FollowUp/GetCalenderByFilters", payload);


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // Parse the data field if it's a string
      let followUpData = response.data.data;
      if (typeof followUpData === 'string') {
        try {
          followUpData = JSON.parse(followUpData);
        } catch (parseError) {
          console.error("Error parsing follow-up data:", parseError);
          followUpData = [];
        }
      }

      if (!Array.isArray(followUpData)) {
        followUpData = [];
      }

      // Transform the data for calendar consumption
      const transformedData = {};
      followUpData.forEach(dayData => {
        // FollowUpDay is already in YYYY-MM-DD format, no need to split
        const dateKey = dayData.FollowUpDay;
        transformedData[dateKey] = {
          date: dayData.FollowUpDay,
          weekDayName: dayData.WeekDayName,
          totalFollowUps: dayData.TotalFollowUps,
          pendingFollowUps: dayData.PendingFollowUps,
          completedFollowUps: dayData.CompletedFollowUps,
          cancelledFollowUps: dayData.CancelledFollowUps,
          rescheduledFollowUps: dayData.RescheduledFollowUps,
          details: dayData.FollowUpDetails?.map(detail => ({
            id: detail.FollowUpId || detail.Id, // Use the actual follow-up ID from backend
            userId: detail.UserId,
            leadname: detail.LeadName, // Lead name from new API
            status: detail.StatusName || detail.Status, // Use StatusName from API
            statusId: detail.StatusId, // Include status ID
            outcomeId: detail.OutcomeId, // Include outcome ID if available
            eventName: detail.EventName, // Event name if available
            remarks: detail.Remarks,
            notes: detail.Notes,
            activityName: detail.ActivityName || "",
            activityTitle: detail.ActivityTitle || "",
            title: detail.title || "",
            preferredDeliveryDate: detail.PreferredDeliveryDate || detail.preferredDeliveryDate || null,
            preferredDeliveryTime: detail.PreferredDeliveryTime || detail.preferredDeliveryTime || null,
            scheduledTime: (() => {
              const t = detail.ScheduledTime;
              if (!t) return '';
              const [h, m] = t.split(':');
              let hours = parseInt(h, 10);
              const minutes = String(parseInt(m, 10)).padStart(2, '0');
              const ampm = hours >= 12 ? 'PM' : 'AM';
              hours = hours % 12;
              hours = hours ? hours : 12;
              return `${hours}:${minutes} ${ampm}`;
            })(),
            leadId: detail.LeadId, // Include lead ID if available
            LeadId: detail.LeadId, // Alternative lead ID field for compatibility
            priority: detail.Priority, // Include priority from API
            assignedTo: detail.AssignedTo, // Include assigned to from API
            channelId: detail.ChannelId, // Include channel ID
            ChannelName: detail.ChannelName || detail.channelName, // Include channel name for type resolution
            type: detail.Type || detail.ChannelId, // Include type/channel if available
            email: detail?.Email, // Alternative email field for compatibility
            phoneNumber: detail?.PhoneNumber, // Alternative phone field for compatibility
            isApplicant: detail.IsApplicant, // Whether this is an applicant or lead
          })) || []
        };
      });

      return {
        success: true,
        data: transformedData,
        rawData: followUpData,
        totalRecords: response.data.totalRecords || followUpData.length,
        message: response.data.message || "Calendar follow-ups fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch calendar follow-ups");
    }
  } catch (error) {
    console.error("Error fetching calendar follow-ups:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch calendar follow-ups";

    toast.error("Failed to load calendar data", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: {},
      rawData: [],
      totalRecords: 0
    };
  }
};

/**
 * Get a flat paginated list of follow-ups for the Activities List view.
 * Uses the same endpoint as the calendar: POST /api/FollowUp/GetCalenderByFilters
 * but without date range - only passes { type, pageNumber, pageSize }.
 *
 * @param {Object} [params={}]
 * @param {number} [params.type=0]        – 0=All, 1=Leads, 2=Applicants
 * @param {number} [params.pageNumber=1]
 * @param {number} [params.pageSize=50]
 */
export const getFollowUpsList = async ({
  type = 0,
  pageNumber = 1,
  pageSize = 100,
  search = "",
  statusFilter = [],
  assignToFilter = [],
  activityTypeFilter = [],
  sortBy = "",
  sortOrder = "",
} = {}) => {
  try {
    const response = await axiosInstance.post(
      "/api/FollowUp/GetCalenderByFilters",
      {
        type,
        pageNumber,
        pageSize,
        Search: cleanSearchValue(search),
        statusFilter: statusFilter?.length ? statusFilter : null,
        assigneeFilter: assignToFilter?.length ? assignToFilter : null,
        activityTypeFilter: activityTypeFilter?.length ? activityTypeFilter : null,
        sortColumn: sortBy || null,
        sortDirection: sortOrder || null,
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let raw = responseData.data;
      if (typeof raw === "string") {
        try { raw = JSON.parse(raw); } catch (_) { raw = []; }
      }

      // The endpoint may return either a flat array or the same date-keyed structure.
      // Flatten both shapes into a single array for the table.
      let list = [];

      if (Array.isArray(raw)) {
        // Each element may be a dayData object with FollowUpDetails, or a flat follow-up item
        raw.forEach((item) => {
          if (item.FollowUpDetails && Array.isArray(item.FollowUpDetails)) {
            // Date-keyed shape - flatten the details
            item.FollowUpDetails.forEach((detail) => {
              list.push({ ...detail, FollowUpDay: item.FollowUpDay });
            });
          } else {
            list.push(item);
          }
        });
      }

      const data = list.map((item) => ({
        id: item.FollowUpId,
        leadId: item.LeadId,
        status: item.StatusName || "Pending",
        activityTitle : item.ActivityTitle || "-",
        type: item.ChannelName || "-",
        title: item.Title || "-",
        channelId: item.ChannelId || "-",
        leadName: item.LeadName || "-",
        activityName: item.ActivityName || "-",
        preferredDeliveryDate: item.PreferredDeliveryDate || "-",
        preferredDeliveryTime: item.PreferredDeliveryTime || "-",
        assignedUser: item.FullName || "-",
        attendees: item.Attendees || "-",
        scheduledDate: item.ScheduledDate
          ? new Date(item.ScheduledDate).toLocaleDateString("en-US", {
              month: "short", day: "numeric", year: "numeric",
            })
          : "-",
        notes: item.Remarks ||  "",
        cellPhone: item.PhoneNumber  || "",
        suburb: item.Suburb || "",
        email: item.Email || "",
        phoneNumber: item.PhoneNumber  || "",
        state: item.State || "",
        streetAddress: item.StreetAddress  || "",
        postalCode: item.PostalCode || "",
        lastContacted: item.LastContactedDate
          ? (typeof item.LastContactedDate === "string"
              ? (item.LastContactedDate.endsWith("Z") || item.LastContactedDate.includes("+")
                  ? item.LastContactedDate
                  : `${item.LastContactedDate}Z`)
              : item.LastContactedDate)
          : "-",
        createdBy: item.CreatedByName || item.CreatedBy || "-",
        modifiedBy: item.ModifiedByName || item.ModifiedBy || "-",
        modifiedDate: item.ModifiedDate
          ? (typeof item.ModifiedDate === "string"
              ? (item.ModifiedDate.endsWith("Z") || item.ModifiedDate.includes("+")
                  ? item.ModifiedDate
                  : `${item.ModifiedDate}Z`)
              : item.ModifiedDate)
          : "-",
        _raw: item,
        createdDate: item.CreatedDate
          ? (typeof item.CreatedDate === "string"
              ? (item.CreatedDate.endsWith("Z") || item.CreatedDate.includes("+")
                  ? item.CreatedDate
                  : `${item.CreatedDate}Z`)
              : item.CreatedDate)
          : "-",
      }));

      return {
        success: true,
        data,
        totalRecords: responseData.totalRecords ?? list.length,
      };
    }

    throw new Error(responseData.message || "Failed to fetch activities list");
  } catch (error) {
    console.error("Error fetching follow-ups list:", error);
    const msg = error.response?.data?.message || error.message || "Failed to fetch activities list";
    toast.error("Failed to load activities", { description: msg, duration: 5000 });
    return { success: false, error: msg, data: [], totalRecords: 0 };
  }
};

export const getActivityById = async (followUpId) => {
  try {
    if (!followUpId) throw new Error("Follow-up ID is required");

    const response = await axiosInstance.get(
      `/api/FollowUp/GetActivityById?FollowUpId=${followUpId}`
    );
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (_) { data = null; }
      }

      // Parse nested JSON strings returned by SQL FOR JSON PATH sub-queries
      if (data) {
        if (typeof data.EmailAttachments === "string") {
          try { data.EmailAttachments = JSON.parse(data.EmailAttachments); } catch (_) { data.EmailAttachments = []; }
        }
        if (typeof data.Attendees === "string") {
          try { data.Attendees = JSON.parse(data.Attendees); } catch (_) { data.Attendees = []; }
        }
        if (data.ScheduledDate && data.ScheduledTime) {
          // Keep raw ScheduledTime as-is; consumers should format without timezone conversion
          data.ScheduledTimeLocal = data.ScheduledTime;
        }
      }

      return { success: true, data, message: responseData.message };
    }

    throw new Error(responseData.message || "Failed to fetch activity details");
  } catch (error) {
    console.error("Error fetching activity by ID:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch activity details";
    return { success: false, error: errorMessage, data: null };
  }
};

// Get follow-ups by month and year for calendar view (legacy API - kept for backward compatibility)
export const getFollowUpsByMonthAndYear = async (month, year) => {
  try {

    const response = await axiosInstance.get("/api/FollowUp/GetFollowupsBymonthAndyear", {
      params: {
        Month: month,
        Year: year
      }
    });


    if (response.data.statusCode === 200 && response.data.status === "Success") {
      // Parse the data field if it's a string
      let followUpData = response.data.data;
      if (typeof followUpData === 'string') {
        try {
          followUpData = JSON.parse(followUpData);
        } catch (parseError) {
          console.error("Error parsing follow-up data:", parseError);
          followUpData = [];
        }
      }

      if (!Array.isArray(followUpData)) {
        followUpData = [];
      }

      // Transform the data for calendar consumption
      const transformedData = {};
      followUpData.forEach(dayData => {
        // FollowUpDay is already in YYYY-MM-DD format, no need to split
        const dateKey = dayData.FollowUpDay;
        transformedData[dateKey] = {
          date: dayData.FollowUpDay,
          weekDayName: dayData.WeekDayName,
          totalFollowUps: dayData.TotalFollowUps,
          pendingFollowUps: dayData.PendingFollowUps,
          completedFollowUps: dayData.CompletedFollowUps,
          cancelledFollowUps: dayData.CancelledFollowUps,
          rescheduledFollowUps: dayData.RescheduledFollowUps,
          details: dayData.FollowUpDetails?.map(detail => ({
            id: detail.FollowUpId || detail.Id, // Use the actual follow-up ID from backend
            userId: detail.UserId,
            userName: detail.UserName,
            leadname: detail.leadname, // Lead name from new API
            LeadName: detail.LeadName, // Alternative lead name field
            fullName: detail.FullName, // Assigned user's full name
            status: detail.StatusName || detail.Status, // Use StatusName from API
            statusId: detail.StatusId, // Include status ID
            outcomeId: detail.OutcomeId, // Include outcome ID if available
            eventName: detail.EventName, // Event name if available
            remarks: detail.Remarks,
            notes: detail.Notes,
            scheduledTime: (() => {
              const t = detail.ScheduledTime;
              if (!t) return '';
              const [h, m] = t.split(':');
              let hours = parseInt(h, 10);
              const minutes = String(parseInt(m, 10)).padStart(2, '0');
              const ampm = hours >= 12 ? 'PM' : 'AM';
              hours = hours % 12;
              hours = hours ? hours : 12;
              return `${hours}:${minutes} ${ampm}`;
            })(),
            leadId: detail.LeadId, // Include lead ID if available
            LeadId: detail.LeadId, // Alternative lead ID field for compatibility
            priority: detail.Priority, // Include priority from API
            assignedTo: detail.AssignedTo, // Include assigned to from API
            channelId: detail.ChannelId, // Include channel ID
            ChannelName: detail.ChannelName || detail.channelName, // Include channel name for type resolution
            type: detail.Type || detail.ChannelId, // Include type/channel if available
            Email: detail.Email, // Include email from API
            email: detail.Email, // Alternative email field for compatibility
            PhoneNumber: detail.PhoneNumber, // Include phone number from API
            phoneNumber: detail.PhoneNumber, // Alternative phone field for compatibility
            mobile: detail.PhoneNumber, // Alternative mobile field for compatibility
            isApplicant: detail.IsApplicant, // Whether this is an applicant or lead
            IsApplicant: detail.IsApplicant // Alternative field for compatibility
          })) || []
        };
      });

      return {
        success: true,
        data: transformedData,
        rawData: followUpData,
        totalRecords: response.data.totalRecords || followUpData.length,
        message: response.data.message || "Monthly follow-ups fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch monthly follow-ups");
    }
  } catch (error) {
    console.error("Error fetching monthly follow-ups:", error);

    const errorMessage = error.response?.data?.message || error.message || "Failed to fetch monthly follow-ups";

    toast.error("Failed to load calendar data", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: {},
      rawData: [],
      totalRecords: 0
    };
  }
};

// Fetch all activity base types (channels)
export const getActivityBaseTypes = async () => {
  try {
    const response = await axiosInstance.get("/api/FollowUp/GetActivityBaseTypes");
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let baseTypesData = [];
      if (typeof responseData.data === "string") {
        try {
          baseTypesData = JSON.parse(responseData.data);
        } catch (parseError) {
          console.error("Error parsing activity base types data:", parseError);
        }
      } else if (Array.isArray(responseData.data)) {
        baseTypesData = responseData.data;
      }

      const baseTypes = baseTypesData.map(type => ({
        id: type.Id,
        name: type.Name
      }));

      return {
        success: true,
        data: baseTypes,
        message: responseData.message || "Base types fetched successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch activity base types");
    }
  } catch (error) {
    console.error("Error fetching activity base types:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to fetch activity base types",
      data: []
    };
  }
};

// Create or update a custom activity type
export const upsertLeadActivityType = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/FollowUp/UpsertLeadActivityType", payload);
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Activity type saved successfully"
      };
    } else {
      return {
        success: false,
        error: responseData.message || "Failed to save activity type"
      };
    }
  } catch (error) {
    console.error("Error saving activity type:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to save activity type"
    };
  }
};

// Delete a custom activity type
export const deleteLeadActivityType = async (id) => {
  try {
    const response = await axiosInstance.post(`/api/FollowUp/DeleteLeadActivityType/${id}`);
    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "Activity type deleted successfully"
      };
    } else {
      return {
        success: false,
        error: responseData.message || "Failed to delete activity type"
      };
    }
  } catch (error) {
    console.error("Error deleting activity type:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to delete activity type"
    };
  }
};

// Bulk Actions Dispatcher
export const bulkFollowUpActionDispatcher = async (payload) => {
  try {
    const response = await axiosInstance.post("/api/FollowUp/BulkAction", payload);
    const responseData = response.data;

    if (responseData.statusCode === 200 && (responseData.status === "Success" || responseData.status?.toLowerCase() === "success")) {
      return {
        success: true,
        data: responseData.data,
        message: responseData.message || "Bulk action performed successfully",
      };
    } else {
      return {
        success: false,
        error: responseData.message || "Failed to perform bulk action"
      };
    }
  } catch (error) {
    console.error("Error performing bulk follow-up action:", error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || "Failed to perform bulk action"
    };
  }
};

/**
 * Upsert a Custom Activity (personal reminder not tied to Lead or Job).
 * POST /api/FollowUp/UpsertCustomActivity
 * 
 * @param {Object} payload 
 */
export const upsertCustomActivity = async (payload) => {
  try {
    const scheduledDateStr = payload.scheduledDate
      ? (payload.scheduledDate instanceof Date ? getLocalISODate(payload.scheduledDate) : String(payload.scheduledDate).split("T")[0])
      : getLocalISODate(new Date());

    let scheduledTimeStr = payload.scheduledTime || "09:00";
    if (scheduledTimeStr && scheduledTimeStr.length === 5) {
      scheduledTimeStr = `${scheduledTimeStr}:00`;
    }

    const apiPayload = {
      followUpId: payload.followUpId || payload.id || null,
      assignedTo: payload.assignedTo || getUserIdFromToken(),
      title: payload.title || payload.Title || null,
      activityName: payload.activityName ||  null,
      notes: payload.notes || null,
     remarks:payload.remarks || null,
      scheduledDate: scheduledDateStr,
      scheduledTime: scheduledTimeStr,
      reminderBeforeMinutes: payload.reminderBeforeMinutes !== undefined && payload.reminderBeforeMinutes !== "" ? Number(payload.reminderBeforeMinutes) : null,
      clearReminderMinutes: payload.clearReminderMinutes || false,
      attendeeIds: Array.isArray(payload.attendeeIds) ? payload.attendeeIds : null,
      priority: payload.priority || "Normal",
      statusId: payload.statusId ? Number(payload.statusId) : 1,
      isJobActivity: payload.isJobActivity || false,
    };

    const response = await axiosInstance.post("/api/FollowUp/UpsertCustomActivity", apiPayload);
    const responseData = response.data;

    if (responseData.statusCode === 200 && (responseData.status === "Success" || responseData.status?.toLowerCase() === "success")) {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing upsertCustomActivity result:", e);
        }
      }
      return {
        success: true,
        data,
        message: responseData.message || "Custom activity saved successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to save custom activity");
    }
  } catch (error) {
    console.error("Error saving custom activity:", error);
    const errorMessage = error.response?.data?.message || error.message || "Failed to save custom activity";
    toast.error("Failed to save custom activity", {
      description: errorMessage,
      duration: 5000,
    });
    return {
      success: false,
      error: errorMessage,
    };
  }
};

